// Questions sur la base (spec § 10 bis) : lecture de la réponse de la fonction base-assistant. La réponse cite les
// fiches, sociétés, affaires et personnes par des repères ([[P:AEG-000123]]…), remplacés ici par un lien dont le
// libellé vient des résultats des outils, jamais du modèle.
import type { SavedSearchCriteria } from "./savedSearchCriteria";

export type QuestionEntity = {
  kind: "P" | "C" | "D" | "N";
  key: string;
  label: string | null;
  project_id: string | null;
};

export type ToolCall = {
  name: string;
  args: Record<string, unknown>;
  rows?: Record<string, unknown>[];
  error?: string;
};

export type AssistantFilters = Record<string, unknown>;

export type QuestionAnswer = {
  question_id: number | null;
  conversation_id?: number;
  // Réponse relue dans l'historique : date de la question et note donnée.
  asked_at?: string;
  rating?: 1 | -1 | null;
  answer: string | null;
  interpretation: string;
  caveats: string[];
  refused: boolean;
  discarded: boolean;
  entities: QuestionEntity[];
  results: ToolCall[];
  model: string;
};

const UPSTREAM = ["Concept", "Feasibility study", "Design", "Pre-construction"];
const PROJECT_TOOLS = new Set([
  "count_projects",
  "search_projects",
  "top_companies",
  "recent_changes",
  "find_people",
]);

const strings = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((v): v is string => typeof v === "string" && v !== "")
    : [];
const phases = (value: unknown) => [
  ...new Set(
    strings(value).flatMap((p) => (p === "upstream" ? UPSTREAM : [p])),
  ),
];
const present = (value: unknown) =>
  value !== undefined &&
  value !== null &&
  value !== "" &&
  !(Array.isArray(value) && !value.length);

/** Filtres du dernier outil appelé sur les fiches (celui qui a donné la réponse). */
export const lastFilters = (
  calls: Pick<ToolCall, "name" | "args">[],
): AssistantFilters | null => {
  for (const call of [...calls].reverse()) {
    if (PROJECT_TOOLS.has(call.name))
      return (call.args.filters as AssistantFilters | undefined) ?? {};
  }
  return null;
};

// Une valeur seule, ou une liste PostgREST (« in ») ; les valeurs à espaces sont mises entre guillemets.
const listValue = (
  filter: Record<string, unknown>,
  field: string,
  values: string[],
) => {
  if (values.length === 1) filter[field] = values[0];
  else if (values.length > 1) {
    const quote = values.some((v) => /[\s,]/.test(v));
    filter[`${field}@in`] =
      `(${values.map((v) => (quote ? `"${v}"` : v)).join(",")})`;
  }
};

/** Filtres de la liste des projets équivalents à ceux de l'assistant, et ceux qu'elle ne sait pas appliquer. */
export const listFilterFromAssistant = (filters: AssistantFilters) => {
  const filter: Record<string, unknown> = {};
  const missing: string[] = [];
  for (const [key, value] of Object.entries(filters)) {
    if (!present(value)) continue;
    switch (key) {
      case "countries":
        listValue(filter, "country", strings(value));
        break;
      case "categories":
        listValue(filter, "category", strings(value));
        break;
      case "phases":
        listValue(filter, "phase", phases(value));
        break;
      case "min_score":
        filter["score@gte"] = value;
        break;
      case "created_after":
        filter["created_at@gte"] = value;
        break;
      case "created_before":
        filter["created_at@lt"] = value;
        break;
      case "news_after":
        filter["last_news_at@gte"] = value;
        break;
      case "to_specify":
        filter.to_specify = value;
        break;
      // Comme l'assistant : seules les fiches chiffrées, au même montant en euros.
      case "min_eur":
        filter["budget_eur@gte"] = value;
        break;
      case "max_eur":
        filter["budget_eur@lte"] = value;
        break;
      default:
        missing.push(key);
    }
  }
  // Comme l'assistant : sans catégorie demandée, « autre » est masquée.
  if (!present(filters.categories)) filter["category@neq"] = "other";
  return { filter, missing };
};

/** Critères d'une veille équivalents aux filtres de l'assistant, et ceux qu'une veille ne sait pas suivre. */
export const criteriaFromAssistant = (filters: AssistantFilters) => {
  const criteria: SavedSearchCriteria = {
    countries: strings(filters.countries),
    categories: strings(filters.categories),
    phases: phases(filters.phases),
    min_score: typeof filters.min_score === "number" ? filters.min_score : null,
    min_budget_eur:
      typeof filters.min_eur === "number" && filters.min_eur > 0
        ? filters.min_eur
        : null,
  };
  const known = new Set([
    "countries",
    "categories",
    "phases",
    "min_score",
    "min_eur",
  ]);
  const missing = Object.entries(filters)
    .filter(([key, value]) => !known.has(key) && present(value))
    .map(([key]) => key);
  return { criteria, missing };
};

export const entityPath = (entity: QuestionEntity): string | null => {
  switch (entity.kind) {
    case "P":
      return entity.project_id ? `/projects/${entity.project_id}/show` : null;
    case "C":
      return `/companies/${entity.key}/show`;
    case "D":
      return `/deals/${entity.key}/show`;
    case "N":
      return `/contacts/${entity.key}/show`;
  }
};

export type AnswerPart = { text: string } | { entity: QuestionEntity };
export type AnswerBlock =
  | { type: "paragraph"; parts: AnswerPart[] }
  | { type: "list"; items: AnswerPart[][] };

const MARKER = /\[\[([PCDN]):([A-Za-z0-9-]+)\]\]/g;

const parts = (line: string, entities: QuestionEntity[]): AnswerPart[] => {
  const found: AnswerPart[] = [];
  let last = 0;
  for (const match of line.matchAll(MARKER)) {
    if (match.index > last) found.push({ text: line.slice(last, match.index) });
    const entity = entities.find(
      (e) => e.kind === match[1] && e.key === match[2],
    );
    found.push(entity ? { entity } : { text: match[2] });
    last = match.index + match[0].length;
  }
  if (last < line.length) found.push({ text: line.slice(last) });
  return withoutRepeats(found);
};

const escape = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Le modèle répète parfois le nom après le repère (« [[P:…]] (Nom, score 86) ») : le lien porte déjà ce nom.
const withoutRepeats = (found: AnswerPart[]) =>
  found
    .map((part, index) => {
      const previous = found[index - 1];
      if (!("text" in part) || !previous || !("entity" in previous))
        return part;
      const label = previous.entity.label;
      if (!label) return part;
      const name = escape(label.trim());
      const text = part.text
        .replace(new RegExp(`^\\s*\\(\\s*${name}\\s*\\)`, "i"), "")
        .replace(new RegExp(`^(\\s*\\()\\s*${name}\\s*,\\s*`, "i"), "$1")
        .replace(new RegExp(`^\\s+${name}(?![\\p{L}\\p{N}])`, "iu"), "");
      return { text };
    })
    .filter((part) => !("text" in part) || part.text !== "");

/** Paragraphes et listes de la réponse, chaque repère connu devenant un lien. */
export const answerBlocks = (
  answer: string,
  entities: QuestionEntity[],
): AnswerBlock[] => {
  const blocks: AnswerBlock[] = [];
  for (const raw of answer.replace(/\*\*/g, "").split("\n")) {
    const line = raw.trim();
    if (!line) continue;
    const item = line.match(/^[*-]\s+(.*)$/);
    if (item) {
      const previous = blocks.at(-1);
      if (previous?.type === "list")
        previous.items.push(parts(item[1], entities));
      else blocks.push({ type: "list", items: [parts(item[1], entities)] });
    } else {
      blocks.push({ type: "paragraph", parts: parts(line, entities) });
    }
  }
  return blocks;
};

/** Réponse en texte simple (fiches nommées avec leur numéro), renvoyée avec la question de suivi. */
export const plainAnswer = (answer: string, entities: QuestionEntity[]) =>
  answer.replace(MARKER, (_, kind: string, key: string) => {
    const entity = entities.find((e) => e.kind === kind && e.key === key);
    if (!entity?.label) return key;
    return kind === "P" ? `${entity.label} (${key})` : entity.label;
  });

/** Question enregistrée dans l'historique (table `assistant_questions`). */
export type StoredQuestion = {
  id: number;
  question: string;
  interpretation: string | null;
  answer: string | null;
  caveats: string[] | null;
  entities: QuestionEntity[] | null;
  tool_calls: (Pick<ToolCall, "name" | "args" | "error"> & {
    rows?: number;
  })[];
  refused: boolean;
  discarded: boolean;
  rating: 1 | -1 | null;
  model: string;
  created_at: string;
};

/** Réponse relue : le texte, les réserves et les filtres de ce jour-là ; les lignes brutes ne sont pas gardées. */
export const answerFromStored = (row: StoredQuestion): QuestionAnswer => ({
  question_id: row.id,
  answer: row.answer,
  interpretation: row.interpretation ?? "",
  caveats: row.caveats ?? [],
  refused: row.refused,
  discarded: row.discarded,
  entities: row.entities ?? [],
  results: (row.tool_calls ?? []).map(({ name, args, error }) =>
    error ? { name, args, error } : { name, args },
  ),
  model: row.model,
  asked_at: row.created_at,
  rating: row.rating,
});

export type HistoryGroup = "today" | "yesterday" | "week" | "older";

/** Rangement d'une conversation dans l'historique, selon le jour de sa dernière question (heure locale). */
export const historyGroup = (
  updatedAt: string,
  now = new Date(),
): HistoryGroup => {
  const day = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((day(now) - day(new Date(updatedAt))) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return "week";
  return "older";
};
